import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from 'helpers/api_helper';

// Types
export interface AgentUser {
  id: number;
  user_id: number;
  mobile_number: string;
  email?: string;
  first_name: string;
  last_name: string;
  role: string;
  points: number;
  status: 'active' | 'inactive' | 'blocked';
  login_type: 'password' | 'mpin';
  joined_date: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentUsersResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  results: AgentUser[];
}

export interface AgentUsersState {
  users: AgentUser[];
  loading: boolean;
  error: string | null;
  pagination: {
    count: number;
    page: number;
    page_size: number;
  };
  lastFetched: string | null;
}

const initialState: AgentUsersState = {
  users: [],
  loading: false,
  error: null,
  pagination: {
    count: 0,
    page: 1,
    page_size: 25,
  },
  lastFetched: null,
};

// Async thunk for fetching agent users
export const fetchAgentUsers = createAsyncThunk(
  'agentUsers/fetchAgentUsers',
  async (params: { page?: number; page_size?: number } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, page_size = 25 } = params;
      const response = await api.get(`/agents/mapped-users/`, {
        params: {
          page,
          page_size,
        },
      });
      
      return response.data as AgentUsersResponse;
    } catch (error: any) {
      console.error('Error fetching agent users:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch agent users'
      );
    }
  }
);

// Fetch users registered under the current agent via referrals endpoint
export const fetchAgentUsersFromReferrals = createAsyncThunk(
  'agentUsers/fetchAgentUsersFromReferrals',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await api.get('/referrals/users/');
      const raw = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      const results = raw.map((u: any, idx: number) => ({
        id: u.id ?? idx,
        user_id: u.user_id ?? u.id ?? idx,
        mobile_number: u.mobile_number ?? '',
        email: u.email ?? '',
        first_name: u.first_name ?? '',
        last_name: u.last_name ?? '',
        role: 'User',
        points: Number(u.points ?? 0),
        status: (u.is_active === false || u.status === 'inactive' || u.status === 'blocked') ? 'inactive' : 'active',
        login_type: (u.login_type === 'mpin' ? 'mpin' : 'password'),
        joined_date: u.created_at ?? u.created ?? '',
        last_login: u.last_login ?? '',
        created_at: u.created_at ?? '',
        updated_at: u.updated_at ?? '',
      }));
      const payload: AgentUsersResponse = {
        success: true,
        count: results.length,
        page: 1,
        page_size: results.length || 25,
        results,
      };
      return payload;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch agent users (referrals)');
    }
  }
);

// Async thunk for updating user points
export const updateUserPoints = createAsyncThunk(
  'agentUsers/updateUserPoints',
  async ({ userId, points }: { userId: number; points: number }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/agents/mapped-users/${userId}/points/`, {
        points,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating user points:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to update user points'
      );
    }
  }
);

// Async thunk for blocking/unblocking user
export const toggleUserStatus = createAsyncThunk(
  'agentUsers/toggleUserStatus',
  async ({ userId, status }: { userId: number; status: 'active' | 'blocked' }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/agents/mapped-users/${userId}/status/`, {
        status,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to update user status'
      );
    }
  }
);

const agentUsersSlice = createSlice({
  name: 'agentUsers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUsers: (state) => {
      state.users = [];
      state.pagination = {
        count: 0,
        page: 1,
        page_size: 25,
      };
      state.lastFetched = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch agent users
    builder
      .addCase(fetchAgentUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.users = action.payload.results;
        state.pagination = {
          count: action.payload.count,
          page: action.payload.page,
          page_size: action.payload.page_size,
        };
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchAgentUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch via referrals
    builder
      .addCase(fetchAgentUsersFromReferrals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentUsersFromReferrals.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.users = action.payload.results;
        state.pagination = {
          count: action.payload.count,
          page: action.payload.page,
          page_size: action.payload.page_size,
        };
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchAgentUsersFromReferrals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update user points
    builder
      .addCase(updateUserPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserPoints.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Update the user in the list
        const updatedUser = action.payload;
        const index = state.users.findIndex(user => user.id === updatedUser.id);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...updatedUser };
        }
      })
      .addCase(updateUserPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Toggle user status
    builder
      .addCase(toggleUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Update the user in the list
        const updatedUser = action.payload;
        const index = state.users.findIndex(user => user.id === updatedUser.id);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...updatedUser };
        }
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUsers, setPage } = agentUsersSlice.actions;
export default agentUsersSlice.reducer;
